"use client";

import React from 'react';
import { Link } from 'react-router-dom';
import * as Icon from "@phosphor-icons/react/dist/ssr";

const PageNotFound = () => {
  document.title = "Page non trouvée | Kissarity";

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-white via-pink-50 to-pink-100 flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Animated 404 Number */}
          <div className="relative mb-8">
            <div className="text-9xl md:text-[12rem] font-bold text-pink opacity-20 select-none">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-8xl font-bold text-pink animate-pulse">
                404
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Oups ! Page introuvable
            </h1>
            
            <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
              La page que vous recherchez semble avoir disparu dans notre garde-robe virtuelle. 
              Peut-être qu'elle est partie faire du shopping ? 🛍️
            </p>

            {/* Decorative Elements */}
            <div className="flex justify-center items-center gap-4 my-8">
              <div className="w-3 h-3 bg-pink rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 bg-pink rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 bg-pink rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
              <Link
                to="/"
                className="group relative px-8 py-4 bg-pink text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Icon.House size={20} />
                Retour à l'accueil
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/shop"
                className="group relative px-8 py-4 border-2 border-pink text-pink font-semibold rounded-full hover:bg-pink hover:text-white transition-all duration-300 flex items-center gap-2"
              >
                <Icon.ShoppingBag size={20} />
                Découvrir nos produits
              </Link>
            </div>

            {/* Fun Facts Section */}
            <div className="mt-12 p-6 bg-white rounded-2xl shadow-lg border border-pink-100">
              <h3 className="text-lg font-semibold text-pink mb-4 flex items-center justify-center gap-2">
                <Icon.Lightbulb size={20} />
                Le saviez-vous ?
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <Icon.Star size={16} className="text-pink mt-0.5 flex-shrink-0" />
                  <p>Nos produits sont soigneusement sélectionnés pour vous offrir le meilleur de la mode</p>
                </div>
                <div className="flex items-start gap-2">
                  <Icon.Truck size={16} className="text-pink mt-0.5 flex-shrink-0" />
                  <p>Livraison rapide et gratuite à partir de 750 MAD d'achat</p>
                </div>
                <div className="flex items-start gap-2">
                  <Icon.ShieldCheck size={16} className="text-pink mt-0.5 flex-shrink-0" />
                  <p>Retours gratuits sous 7 jours si vous n'êtes pas satisfait</p>
                </div>
                <div className="flex items-start gap-2">
                  <Icon.Heart size={16} className="text-pink mt-0.5 flex-shrink-0" />
                  <p>Plus de 10,000 clients satisfaits nous font confiance</p>
                </div>
              </div>
            </div>

            {/* Search Suggestion */}
            <div className="mt-8 p-4 bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl border border-pink-200">
              <p className="text-sm text-gray-700 mb-3">
                <Icon.MagnifyingGlass size={16} className="inline mr-2 text-pink" />
                Essayez de rechercher ce que vous cherchez :
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['Robes', 'Tops', 'Pantalons', 'Accessoires', 'Bijoux', 'Chaussures'].map((category) => (
                  <Link
                    key={category}
                    to={`/shop?category=${category.toLowerCase()}`}
                    className="px-3 py-1 bg-white text-pink text-xs font-medium rounded-full border border-pink-200 hover:bg-pink hover:text-white transition-colors duration-200"
                  >
                    {category}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 mb-2">
                Besoin d'aide ? Contactez-nous :
              </p>
              <div className="flex justify-center items-center gap-4 text-sm">
                <a 
                  href="mailto:contact@kissariti.ma" 
                  className="text-pink hover:text-pink-700 transition-colors duration-200 flex items-center gap-1"
                >
                  <Icon.Envelope size={16} />
                  contact@kissariti.ma
                </a>
                <span className="text-gray-300">|</span>
                <a 
                  href="tel:+212500000000" 
                  className="text-pink hover:text-pink-700 transition-colors duration-200 flex items-center gap-1"
                >
                  <Icon.Phone size={16} />
                  +212 5 00 00 00 00
                </a>
              </div>
            </div>
          </div>

          {/* Floating Elements */}
          <div className="fixed top-10 left-10 w-20 h-20 bg-pink opacity-10 rounded-full animate-pulse"></div>
          <div className="fixed top-20 right-20 w-16 h-16 bg-pink opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="fixed bottom-20 left-20 w-12 h-12 bg-pink opacity-10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="fixed bottom-10 right-10 w-24 h-24 bg-pink opacity-10 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        </div>
      </div>
    </>
  );
};

export default PageNotFound;
